import React, { useEffect, useRef, useState } from 'react';
import { Printer, X, Download } from 'lucide-react';
import { siteSettingService } from '../../services/api';

const defaultCompany = {
  name: 'Ortus Food & Beverages',
  tagline: 'FMCG Platform',
  address: '123 Corporate Plaza, Business District, Mumbai, Maharashtra 400001',
  gst: '27AAAAA0000A1Z5',
  logo: null,
};

function InvoicePrint({ invoice, onClose }) {
  const printRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const [company, setCompany] = useState({
    ...defaultCompany,
    gst: invoice?.gst_number || defaultCompany.gst,
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await siteSettingService.index();
        const toMap = (key, fallback) => settings?.find((s) => s.key === key)?.value ?? fallback;
        setCompany({
          name: toMap('site_name', defaultCompany.name),
          tagline: toMap('site_tagline', defaultCompany.tagline),
          address: toMap('contact_address', defaultCompany.address),
          gst: invoice?.gst_number || toMap('gst_number', defaultCompany.gst),
          logo: toMap('site_logo', null),
        });
      } catch (error) {
        console.error('Failed to load company settings', error);
      }
    };

    loadSettings();
  }, [invoice]);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Invoice_${invoice.invoice_number || invoice.order_number}`;
    window.print();
    document.title = originalTitle;
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // For now we use print as PDF which is most reliable across browsers
      // without adding heavy JS libraries like jspdf for now.
      handlePrint();
    } finally {
      setDownloading(false);
    }
  };

  if (!invoice) return null;

  const safeNumber = (value) => {
    const parsed = parseFloat(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const subtotal = safeNumber(invoice.subtotal);
  const tax = safeNumber(invoice.tax);
  const shipping = safeNumber(invoice.shipping_cost);
  const discount = safeNumber(invoice.discount);
  const total = safeNumber(invoice.total);
  const items = invoice.order?.items || [];

  const shippingAddress = invoice.order?.shipping_address;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header Actions */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-900">Invoice Details</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              <Download size={18} />
              {downloading ? 'Preparing...' : 'Download PDF'}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium shadow-lg shadow-red-200"
            >
              <Printer size={18} />
              Print
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-100" ref={printRef}>
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              body * { visibility: hidden; }
              #printable-invoice, #printable-invoice * { visibility: visible; }
              #printable-invoice { 
                position: absolute; 
                left: 0; 
                top: 0; 
                width: 100%;
                padding: 0 !important;
                margin: 0 !important;
              }
              .no-print { display: none !important; }
            }
          ` }} />
          <div id="printable-invoice" className="max-w-3xl mx-auto bg-white shadow-sm p-10 rounded-sm min-h-[11in]">
            {/* Invoice Branding & Info */}
            <div className="flex justify-between items-start mb-12">
              <div>
                {company.logo ? (
                  <img src={company.logo} alt="Company logo" className="h-10 object-contain mb-3" />
                ) : (
                  <h1 className="text-4xl font-black text-red-600 mb-2">{company.name.toUpperCase()}</h1>
                )}
                <p className="text-gray-500 text-sm uppercase tracking-widest font-bold">{company.tagline}</p>
                <div className="mt-6 text-sm text-gray-600 leading-relaxed">
                  <p className="font-bold text-gray-900">{company.name}</p>
                  <p>{company.address}</p>
                  <p>GSTIN: {company.gst || 'Not Provided'}</p>
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 uppercase">Tax Invoice</h2>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500 font-medium">Invoice No:</span> <span className="font-bold">{invoice.invoice_number}</span></p>
                  <p><span className="text-gray-500 font-medium">Order No:</span> <span className="font-bold">{invoice.order?.order_number || invoice.order_id}</span></p>
                  <p><span className="text-gray-500 font-medium">Date:</span> <span className="font-bold">{new Date(invoice.issued_at).toLocaleDateString()}</span></p>
                  <p><span className="text-gray-500 font-medium">Status:</span> <span className="font-bold text-red-600 uppercase">{(invoice.status || 'Paid').toUpperCase()}</span></p>
                </div>
              </div>
            </div>

            {/* Bill To / Ship To */}
            <div className="grid grid-cols-2 gap-12 mb-12">
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Customer Information</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p className="font-bold text-gray-900 text-base">{invoice.user?.name || invoice.order?.shipping_address?.full_name || 'Valued Customer'}</p>
                  <p>{invoice.user?.email || invoice.order?.email || '—'}</p>
                  <p>{invoice.user?.phone || invoice.order?.phone || '—'}</p>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Shipping Address</h4>
                <div className="text-sm text-gray-700 leading-relaxed">
                  {shippingAddress ? (
                    <>
                      <p className="font-bold">{shippingAddress.full_name}</p>
                      <p>{shippingAddress.address_line1 || shippingAddress.address_line_1}</p>
                      {(shippingAddress.address_line2 || shippingAddress.address_line_2) && (
                        <p>{shippingAddress.address_line2 || shippingAddress.address_line_2}</p>
                      )}
                      <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}</p>
                      <p>{shippingAddress.country}</p>
                      <p>Phone: {shippingAddress.phone || '—'}</p>
                    </>
                  ) : (
                    <p className="italic text-gray-400">Shipping details unavailable</p>
                  )}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-12">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="py-4 text-sm font-bold uppercase">Description</th>
                    <th className="py-4 text-sm font-bold uppercase text-center">Qty</th>
                    <th className="py-4 text-sm font-bold uppercase text-right">Unit Price</th>
                    <th className="py-4 text-sm font-bold uppercase text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.length > 0 ? (
                    items.map((item) => (
                      <tr key={item.id}>
                        <td className="py-4">
                          <p className="font-bold text-gray-900">{item.product?.name || item.product_name || 'Product'}</p>
                          {(item.variant_id || item.variant) && (
                            <p className="text-xs text-purple-600 font-bold">Variant: {item.variant?.name || item.product_name}</p>
                          )}
                          <p className="text-xs text-gray-500">SKU: {item.product_sku || item.product?.sku || 'N/A'}</p>
                        </td>
                        <td className="py-4 text-center text-gray-700">{item.quantity}</td>
                        <td className="py-4 text-right text-gray-700">₹{safeNumber(item.unit_price ?? item.price).toFixed(2)}</td>
                        <td className="py-4 text-right font-bold text-gray-900">₹{safeNumber(item.total_price ?? item.subtotal).toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-gray-400">No items found for this order.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">Subtotal</span>
                  <span className="text-gray-900 font-bold">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">GST (18%)</span>
                  <span className="text-gray-900 font-bold">₹{tax.toFixed(2)}</span>
                </div>
                {shipping > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 font-medium">Shipping</span>
                    <span className="text-gray-900 font-bold">₹{shipping.toFixed(2)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span className="font-medium">Discount</span>
                    <span className="font-bold">-₹{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t-2 border-gray-900">
                  <span className="text-base font-black uppercase">Total</span>
                  <span className="text-xl font-black text-red-600">₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Footer Notes */}
            <div className="mt-16 pt-8 border-t border-gray-100">
              <p className="text-center text-gray-400 text-xs font-medium">
                Thank you for choosing Ortus. This is a computer generated invoice and does not require a signature.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoicePrint;
